import { Box, Chip, InputBase, Stack, Tooltip, Typography, styled, useTheme } from '@mui/material'
import { Check as CheckIcon, Close as CloseIcon, EditOutlined } from '@mui/icons-material'
import React, { useState } from 'react'
import { IFile } from 'src/generated-types'
import { flex } from 'src/styles/flexCss'
import { FileUrlDocumentLoader, FileUrlImageLoader } from '../input/CommonFileUploader'
import { padding } from 'src/styles/paddingCss'
import { bytesToSize, getThemeColor, isFileImage, shortenFileName } from 'src/utils'
import { DeleteOutline, PendingActions } from '@mui/icons-material'
import { Flex } from 'src/styles/flexComponent'
import { useTranslation } from 'react-i18next'
import { MUIThemeColors } from 'src/mui-theme'
import { GridActionButton } from '../GridActionButton'
import { convertFirstLetterToUpperCase } from 'src/utils/function'
import { Padding } from 'src/styles/paddingComponent'
import { getUserDetail, UserDetail } from 'src/utils/getUserDetails'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { useGetSharedFileUrl } from 'src/api/file'

interface IDocumentViewer {
  file: IFile
  enablePreview: boolean
  disabled: boolean
  dueDate?: string
  document: boolean
  onFileClick?: (e, file, callbackData) => void
  onEdit?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, file: IFile, callbackData: any) => void
  onDelete?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, file: IFile, callbackData: any) => void
  isDeletable?: boolean
  backgroundColor?: string
  callbackData?: any
  isDraft?: boolean
  draftCaption?: string
  isCompact?: boolean
  title?: string
  enableTitleEdit?: boolean
  onTitleEdit?: (fileId: number, title: string) => void
  maxFileNameLength?: number
}

const DocumentViewer: React.FC<IDocumentViewer> = ({
  file,
  enablePreview,
  disabled,
  document,
  isDeletable,
  onFileClick,
  onEdit,
  onDelete,
  dueDate,
  backgroundColor,
  callbackData,
  isDraft,
  draftCaption,
  isCompact = false,
  title,
  enableTitleEdit = false,
  onTitleEdit,
  maxFileNameLength = 25,
}) => {
  const theme = useTheme()
  const { t } = useTranslation('common')
  const { openImagePreviewDialog, openFilePreviewDialog } = useDialogCtx()
  const { data: sharedUrl } = useGetSharedFileUrl(file?.url)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [editTitleValue, setEditTitleValue] = useState('')

  const handlePreviewClick = (e: React.MouseEvent) => {
    if (!sharedUrl) return
    e.stopPropagation()
    if (isFileImage(file)) {
      openImagePreviewDialog(sharedUrl, file?.filename, file?.filename)
    } else {
      openFilePreviewDialog(sharedUrl, file?.filename)
    }
  }

  const handleCardClick = (e: React.MouseEvent) => {
    if (onFileClick) {
      onFileClick(e, file, callbackData)
    } else if (enablePreview) {
      handlePreviewClick(e)
    }
  }

  return (
    <DocumentCard
      sx={{
        cursor: onFileClick || enablePreview ? 'pointer' : 'default',
        alignItems: 'center',
        ...(isCompact && { padding: '6px 8px', minWidth: 0 }),
      }}
      style={{ backgroundColor: backgroundColor }}
      onClick={handleCardClick}
    >
      <ThumbnailContainer
        sx={isCompact ? { height: '36px', width: '36px', borderRadius: 0, backgroundColor: 'transparent' } : undefined}
      >
        {isFileImage(file) ? (
          <FileUrlImageLoader
            fileUrl={file?.url}
            openOnClick={false}
            className={isCompact ? 'h-[36px] w-[36px] object-cover' : 'h-[44px] w-[44px] object-cover'}
          />
        ) : (
          <FileUrlDocumentLoader
            contentTypeValue={file?.contentType}
            fileUrl={file?.url}
            openOnClick={false}
            className={isCompact ? 'h-[34px] w-[34px]' : 'h-[26px] w-[26px]'}
          />
        )}
      </ThumbnailContainer>
      <Box flex={1} sx={{ minWidth: 0 }}>
        <Box display="flex" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
          {isEditingTitle ? (
            <>
              <InputBase
                value={editTitleValue}
                onChange={e => setEditTitleValue(e.target.value)}
                size="small"
                sx={{
                  flex: 1,
                  fontSize: '14px',
                  fontWeight: 700,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 1,
                  height: '28px',
                }}
                autoFocus
                onClick={e => e.stopPropagation()}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    onTitleEdit?.(file.id, editTitleValue)
                    setIsEditingTitle(false)
                  }
                  if (e.key === 'Escape') setIsEditingTitle(false)
                }}
              />
              <GridActionButton
                onClick={e => {
                  e.stopPropagation()
                  onTitleEdit?.(file.id, editTitleValue)
                  setIsEditingTitle(false)
                }}
                height="32px"
                width="32px"
                shadowWeight={0}
                disabled={disabled}
                buttonIcon={<CheckIcon sx={{ color: getThemeColor(theme, MUIThemeColors.primaryMain) }} />}
              />
              <GridActionButton
                onClick={e => {
                  e.stopPropagation()
                  setIsEditingTitle(false)
                }}
                height="32px"
                width="32px"
                shadowWeight={0}
                disabled={disabled}
                buttonIcon={<CloseIcon sx={{ color: getThemeColor(theme, MUIThemeColors.primaryMain) }} />}
              />
            </>
          ) : (
            <Tooltip title={file?.filename ?? ''} placement="top-start" enterDelay={400}>
              <Typography variant="body1" fontWeight={600} noWrap sx={{ minWidth: 0 }}>
                {convertFirstLetterToUpperCase(shortenFileName(title || file?.filename, maxFileNameLength).fileName)}
              </Typography>
            </Tooltip>
          )}
        </Box>
        <Flex.Row>
          {file?.contentLength != 0 && (
            <Typography variant="caption" color={getThemeColor(theme, MUIThemeColors.secondaryDark)} fontWeight={500}>
              {bytesToSize(file?.contentLength)}
            </Typography>
          )}
          {file?.contentLength != 0 && file?.createdByUser?.firstName && <Padding.ph4>.</Padding.ph4>}
          {file?.createdByUser?.firstName && (
            <Typography
              variant="caption"
              color={getThemeColor(theme, MUIThemeColors.secondaryDark)}
              fontWeight={500}
              className="pl-2-core"
            >
              {t('createdBy')} {getUserDetail(file?.createdByUser, UserDetail.FullName)}
            </Typography>
          )}
          {dueDate && file?.createdByUser?.firstName && <Padding.ph4>.</Padding.ph4>}
          {dueDate && (
            <Typography
              variant="caption"
              color={getThemeColor(theme, MUIThemeColors.secondaryDark)}
              fontWeight={500}
              className="pl-2-core"
            >
              {t('dueDate')} {dueDate}
            </Typography>
          )}
        </Flex.Row>
      </Box>
      <Flex.Row justifyContent={'center'} alignItems={'center'} gap={1}>
        {isDraft && (
          <Tooltip title={draftCaption || 'Draft'} arrow placement="bottom">
            <Chip
              label="Draft"
              size="small"
              variant="outlined"
              sx={{ cursor: 'default' }}
              onClick={e => e.stopPropagation()}
            />
          </Tooltip>
        )}
        {document && (
          <>
            {onEdit && (
              <GridActionButton
                onClick={e => onEdit(e, file, callbackData)}
                height="32px"
                width="32px"
                disabled={disabled}
                shadowWeight={0}
                buttonIcon={<PendingActions sx={{ color: getThemeColor(theme, MUIThemeColors.secondaryDark) }} />}
              />
            )}
          </>
        )}

        {enableTitleEdit && onTitleEdit && !isEditingTitle && (
          <GridActionButton
            onClick={e => {
              e.stopPropagation()
              setEditTitleValue(title || file?.filename)
              setIsEditingTitle(true)
            }}
            height="32px"
            width="32px"
            shadowWeight={0}
            disabled={disabled}
            buttonIcon={<EditOutlined sx={{ color: getThemeColor(theme, MUIThemeColors.primaryMain) }} />}
          />
        )}
        {isDeletable && onDelete && !isEditingTitle && (
          <GridActionButton
            onClick={e => {
              e.stopPropagation()
              onDelete(e, file, callbackData)
            }}
            height="32px"
            width="32px"
            shadowWeight={0}
            disabled={disabled}
            buttonIcon={<DeleteOutline sx={{ color: getThemeColor(theme, MUIThemeColors.primaryMain) }} />}
          />
        )}
      </Flex.Row>
    </DocumentCard>
  )
}

export default DocumentViewer

//styled components
const DocumentCard = styled(Stack)`
  ${flex.row}
  border:1px solid ${props => getThemeColor(props.theme, MUIThemeColors.secondaryLight)};
  ${padding.p10}
  border-radius: 4px;
  flex: 1;
`

const ThumbnailContainer = styled(Box)`
  height: 44px;
  width: 44px;
  flex-shrink: 0;
  border-radius: 6px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => getThemeColor(props.theme, MUIThemeColors.backgroundMain)};
`
